import React, { useMemo } from "react";
import {
    Card,
    CardContent,
    Box,
    Typography,
    Chip,
    Divider,
    Stack,
    alpha,
    useTheme,
    IconButton,
    TextField,
} from "@mui/material";
import {
    Hotel as HotelIcon,
    Bed as BedIcon,
    MeetingRoom as RoomIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Check as CheckIcon,
} from "@mui/icons-material";
import { calculateHotelStayTotals, formatPrice, getExtrasForStay, calculateExtraAmount } from "../../helpers/quotationCalculations";
import useGetHotelExtrasForSelect from "../../../../../hooks/hotelExtras/useGetHotelExtrasForSelect";
import useGetHotelRoomCategoriesForSelect from "../../../../../hooks/hotelRoomCategories/useGetHotelRoomCategoriesForSelect";
import { formatDate } from "../../../../../utils/programs/dateCalculations";
import ExpectedProfitLabel from "../../helpers/ExpectedProfitLabel";

const RoomNightDailyView = ({
    hotel,
    hotelIndex,
    isArabic,
    paxTypes,
    onUpdateHotelPricing,
    stayTotals,
    extras,
    extraNameMap,
    theme,
}) => {
    const roomStays = hotel.room_stays || [];
    const totalNights = roomStays.length;
    const maxRooms = roomStays.reduce((max, rs) => Math.max(max, rs.rooms_count || 1), 0);
    const totalPax = (paxTypes || []).reduce((sum, pt) => sum + (pt.count || 0), 0);
    const isEditing = hotel.pricing?.is_price_edited === true;
    const { options: roomCategories } = useGetHotelRoomCategoriesForSelect();

    const toggleEdit = () => {
        if (!onUpdateHotelPricing) return;
        const newVal = !isEditing;
        if (newVal) {
            roomStays.forEach((rs, i) => {
                const effectiveSell = rs.custom_sell_price ?? rs.base_sell_price ?? 0;
                const effectiveCost = rs.custom_cost_price ?? rs.base_cost_price ?? 0;
                onUpdateHotelPricing(hotelIndex, `pricing.custom_room_prices.${i}`, parseFloat(effectiveSell));
                onUpdateHotelPricing(hotelIndex, `pricing.custom_room_costs.${i}`, parseFloat(effectiveCost));
            });
            extras.forEach((ex, i) => {
                onUpdateHotelPricing(hotelIndex, `pricing.custom_extra_prices.${i}.sell_price`, parseFloat(ex.custom_sell_price) || 0);
                onUpdateHotelPricing(hotelIndex, `pricing.custom_extra_prices.${i}.cost_price`, parseFloat(ex.custom_cost_price) || 0);
            });
        }
        onUpdateHotelPricing(hotelIndex, 'pricing.is_price_edited', newVal);
    };

    return (
        <>
            {/* Room Stays Table */}
            {roomStays.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.25, fontSize: '0.85rem' }}>
                        {isArabic ? 'إقامات الغرف' : 'Room Stays'}
                    </Typography>
                    <Stack spacing={0.75}>
                        {roomStays.map((rs, i) => {
                            const customSell = hotel.pricing?.custom_room_prices?.[i];
                            const customCost = hotel.pricing?.custom_room_costs?.[i];
                            const sellPrice = customSell !== undefined ? parseFloat(customSell) : (rs.custom_sell_price ?? rs.base_sell_price ?? 0);
                            const costPrice = customCost !== undefined ? parseFloat(customCost) : (rs.custom_cost_price ?? rs.base_cost_price ?? 0);
                            const roomsCount = rs.rooms_count || 1;
                            const rowSellTotal = sellPrice * roomsCount;
                            const rowCostTotal = costPrice * roomsCount;

                            return (
                                <Box
                                    key={rs.id || i}
                                    sx={{
                                        px: 1.5, py: 1, borderRadius: 1,
                                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                                    }}
                                >
                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.8rem', display: 'block', mb: 0.5 }}>
                                        {formatDate(rs.date)} {(() => {
                                            const catId = rs.room_category_id;
                                            const cat = roomCategories?.find(c => String(c.value ?? c.id) === String(catId));
                                            const rawName = cat ? (cat.label || cat.name || rs.room_category_name) : rs.room_category_name;
                                            const name = typeof rawName === 'object' && rawName !== null ? (rawName[isArabic ? 'ar' : 'en'] || rawName.ar || rawName.en || '') : (rawName || '');
                                            return name ? ` - ${name}` : '';
                                        })()} — {isArabic ? 'غرف' : 'Rooms'}: {roomsCount}
                                    </Typography>
                                    {isEditing ? (
                                        <Stack direction="row" spacing={1}>
                                            <TextField
                                                size="small"
                                                label={isArabic ? 'التكلفة' : 'Cost'}
                                                type="number"
                                                value={customCost ?? (rs.custom_cost_price ?? rs.base_cost_price ?? '')}
                                                onChange={(e) =>
                                                    onUpdateHotelPricing(hotelIndex, `pricing.custom_room_costs.${i}`, e.target.value === '' ? '' : e.target.value)
                                                }
                                                sx={{ flex: 1 }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                            />
                                            <TextField
                                                size="small"
                                                label={isArabic ? 'سعر البيع' : 'Sell Price'}
                                                type="number"
                                                value={customSell ?? (rs.custom_sell_price ?? rs.base_sell_price ?? '')}
                                                onChange={(e) =>
                                                    onUpdateHotelPricing(hotelIndex, `pricing.custom_room_prices.${i}`, e.target.value === '' ? '' : e.target.value)
                                                }
                                                sx={{ flex: 1 }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                            />
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {formatPrice(costPrice)} / {formatPrice(sellPrice)} × {roomsCount}
                                            </Typography>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                    {formatPrice(rowCostTotal)} /
                                                </Typography>
                                                <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.9rem' }}>
                                                    {formatPrice(rowSellTotal)}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>

                    {onUpdateHotelPricing && (
                        <Box sx={{ textAlign: 'right', mt: 1 }}>
                            <IconButton size="small" onClick={toggleEdit} sx={{ width: 28, height: 28 }}>
                                {isEditing ? <CheckIcon fontSize="small" color="success" /> : <EditIcon fontSize="small" color="primary" />}
                            </IconButton>
                        </Box>
                    )}
                </Box>
            )}

            {/* Extras */}
            {extras.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.25, fontSize: '0.85rem' }}>
                        {isArabic ? 'الإضافات' : 'Extras'}
                    </Typography>
                    <Stack spacing={0.75}>
                        {extras.map((extra, i) => {
                            const customExtra = hotel.pricing?.custom_extra_prices?.[i];
                            const effectiveSell = customExtra?.sell_price !== undefined ? parseFloat(customExtra.sell_price) : (parseFloat(extra.custom_sell_price) || 0);
                            const effectiveCost = customExtra?.cost_price !== undefined ? parseFloat(customExtra.cost_price) : (parseFloat(extra.custom_cost_price) || 0);
                            const extraWithOverride = { ...extra, custom_sell_price: effectiveSell, custom_cost_price: effectiveCost };
                            const amounts = calculateExtraAmount(extraWithOverride, maxRooms, totalNights, totalPax);
                            const basisLabels = {
                                once: isArabic ? 'مرة واحدة' : 'Once',
                                per_night: isArabic ? 'كل ليلة' : 'Per Night',
                                per_room: isArabic ? 'كل غرفة' : 'Per Room',
                                room_night: isArabic ? 'غرفة/ليلة' : 'Room/Night',
                                per_person: isArabic ? 'للشخص' : 'Per Person',
                            };
                            return (
                                <Box
                                    key={extra.id || i}
                                    sx={{
                                        px: 1.5, py: 1, borderRadius: 1,
                                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', display: 'block', mb: 0.5 }}>
                                        {(() => {
                                            const rawName = extra.name || extra.extra_name || extraNameMap[String(extra.hotel_extra_id)];
                                            const extraName = typeof rawName === 'object'
                                                ? (rawName?.[isArabic ? 'ar' : 'en'] || rawName?.en || rawName?.ar || '')
                                                : (typeof rawName === 'string' ? rawName : '');
                                            return extraName || (isArabic ? `إضافة ${i + 1}` : `Extra ${i + 1}`);
                                        })()}
                                        <Chip
                                            label={basisLabels[extra.pricing_basis] || extra.pricing_basis}
                                            size="small"
                                            sx={{ ml: 0.5, height: 20, fontSize: '0.65rem' }}
                                            variant="outlined"
                                        />
                                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5, fontSize: '0.7rem' }}>
                                            {(() => {
                                                const q = extra.quantity || 1;
                                                const b = extra.pricing_basis;
                                                if (b === 'once') return `×${q}`;
                                                const nights = extra.applicable_dates?.length || extra.days_count || totalNights;
                                                if (b === 'per_night') return `×${q * nights}`;
                                                if (b === 'per_room') return `×${q * maxRooms}`;
                                                if (b === 'room_night') return `×${q * nights * maxRooms}`;
                                                if (b === 'per_person') return `×${q * totalPax}`;
                                                return `×${q}`;
                                            })()}
                                        </Typography>
                                    </Typography>
                                    {isEditing ? (
                                        <Stack direction="row" spacing={1}>
                                            <TextField
                                                size="small"
                                                label={isArabic ? 'التكلفة' : 'Cost'}
                                                type="number"
                                                value={customExtra?.cost_price ?? (parseFloat(extra.custom_cost_price) || '')}
                                                onChange={(e) =>
                                                    onUpdateHotelPricing(hotelIndex, `pricing.custom_extra_prices.${i}.cost_price`, e.target.value === '' ? '' : e.target.value)
                                                }
                                                sx={{ flex: 1 }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                            />
                                            <TextField
                                                size="small"
                                                label={isArabic ? 'سعر البيع' : 'Sell Price'}
                                                type="number"
                                                value={customExtra?.sell_price ?? (parseFloat(extra.custom_sell_price) || '')}
                                                onChange={(e) =>
                                                    onUpdateHotelPricing(hotelIndex, `pricing.custom_extra_prices.${i}.sell_price`, e.target.value === '' ? '' : e.target.value)
                                                }
                                                sx={{ flex: 1 }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                            />
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {formatPrice(effectiveCost)} / {formatPrice(effectiveSell)}
                                            </Typography>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                    {formatPrice(amounts.cost_price)} /
                                                </Typography>
                                                <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.9rem' }}>
                                                    {formatPrice(amounts.sell_price)}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </>
    );
};

const LegacyPerPersonNightView = ({
    hotel,
    hotelIndex,
    isArabic,
    paxTypes,
    onUpdateHotelPricing,
    stayTotals,
    extras,
    extraNameMap,
    theme,
}) => {
    const rooms = hotel.rooms || 1;
    const hotelNights = hotel.nights || 1;
    const totalPax = (paxTypes || []).reduce((sum, pt) => sum + (pt.count || 0), 0);
    const activePaxTypes = useMemo(() =>
        (paxTypes || []).filter(pt => Number(pt.count) > 0),
        [paxTypes]
    );
    const paxPrices = hotel.product_snapshot?.pax_type_prices || [];
    const isEditing = hotel.pricing?.is_price_edited === true;

    const getCustomPrice = (paxTypeId) => {
        if (hotel.pricing?.custom_pax_prices?.hasOwnProperty(paxTypeId)) {
            return hotel.pricing.custom_pax_prices[paxTypeId];
        }
        if (isEditing) {
            const paxConfig = paxPrices.find(p => String(p.pax_type_id) === String(paxTypeId));
            return paxConfig ? parseFloat(paxConfig.price) : '';
        }
        return undefined;
    };

    const getCustomCost = (paxTypeId) => {
        if (hotel.pricing?.custom_pax_costs?.hasOwnProperty(paxTypeId)) {
            return hotel.pricing.custom_pax_costs[paxTypeId];
        }
        if (isEditing) {
            const paxConfig = paxPrices.find(p => String(p.pax_type_id) === String(paxTypeId));
            return paxConfig ? parseFloat(paxConfig.cost_price) : '';
        }
        return undefined;
    };

    const toggleEdit = () => {
        if (!onUpdateHotelPricing) return;
        const newVal = !isEditing;
        if (newVal) {
            activePaxTypes.forEach(pt => {
                const paxConfig = paxPrices.find(p => String(p.pax_type_id) === String(pt.pax_type_id));
                onUpdateHotelPricing(hotelIndex, `pricing.custom_pax_prices.${pt.pax_type_id}`, paxConfig ? parseFloat(paxConfig.price) || '' : '');
                onUpdateHotelPricing(hotelIndex, `pricing.custom_pax_costs.${pt.pax_type_id}`, paxConfig ? parseFloat(paxConfig.cost_price) || '' : '');
            });
        }
        onUpdateHotelPricing(hotelIndex, 'pricing.is_price_edited', newVal);
    };

    return (
        <>
            {activePaxTypes.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: 1 }}>
                            {isArabic ? 'تسعير لكل شخص / ليلة' : 'Per Person/Night Pricing'}
                        </Typography>
                        {onUpdateHotelPricing && (
                            <IconButton size="small" onClick={toggleEdit} sx={{ width: 24, height: 24 }}>
                                {isEditing ? <CloseIcon fontSize="small" color="error" /> : <EditIcon fontSize="small" color="primary" />}
                            </IconButton>
                        )}
                    </Stack>
                    <Stack spacing={0.5}>
                        {activePaxTypes.map(pt => {
                            const count = Number(pt.count) || 0;
                            const paxConfig = paxPrices.find(p => String(p.pax_type_id) === String(pt.pax_type_id)) || {};
                            const rawPaxName = pt.pax_type_name;
                            const paxName =
                                (typeof rawPaxName === 'object' && rawPaxName ? (rawPaxName[isArabic ? 'ar' : 'en'] || rawPaxName.en || rawPaxName.ar || '') : '') ||
                                (typeof rawPaxName === 'string' ? rawPaxName : '') ||
                                `${isArabic ? 'نوع' : 'Type'} ${pt.pax_type_id}`;

                            if (isEditing) {
                                const customPrice = getCustomPrice(pt.pax_type_id);
                                const customCost = getCustomCost(pt.pax_type_id);
                                return (
                                    <Box key={pt.pax_type_id} sx={{ px: 1.5, py: 0.75, borderRadius: 1, bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
                                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}>
                                            {paxName} × {count} × {hotelNights}
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            <TextField
                                                size="small"
                                                label={isArabic ? 'التكلفة' : 'Cost'}
                                                type="number"
                                                value={customCost ?? ''}
                                                onChange={(e) => onUpdateHotelPricing(hotelIndex, `pricing.custom_pax_costs.${pt.pax_type_id}`, e.target.value === '' ? '' : e.target.value)}
                                                sx={{ flex: 1 }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                            />
                                            <TextField
                                                size="small"
                                                label={isArabic ? 'سعر البيع' : 'Sell Price'}
                                                type="number"
                                                value={customPrice ?? ''}
                                                onChange={(e) => onUpdateHotelPricing(hotelIndex, `pricing.custom_pax_prices.${pt.pax_type_id}`, e.target.value === '' ? '' : e.target.value)}
                                                sx={{ flex: 1 }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                            />
                                        </Stack>
                                    </Box>
                                );
                            }

                            const customPrice = getCustomPrice(pt.pax_type_id);
                            const customCost = getCustomCost(pt.pax_type_id);
                            const pricePerPax = (customPrice !== undefined && customPrice !== null && customPrice !== "") ? parseFloat(customPrice) : (parseFloat(paxConfig.price) || 0);
                            const costPerPax = (customCost !== undefined && customCost !== null && customCost !== "") ? parseFloat(customCost) : (parseFloat(paxConfig.cost_price) || 0);
                            const paxSellTotal = pricePerPax * count * hotelNights;
                            const paxCostTotal = costPerPax * count * hotelNights;

                            return (
                                <Box
                                    key={pt.pax_type_id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', flex: 1 }}>
                                        {paxName} ({count})
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mx: 1 }}>
                                        {formatPrice(costPerPax)} / {formatPrice(pricePerPax)} × {count} × {hotelNights}
                                    </Typography>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {formatPrice(paxCostTotal)} /
                                        </Typography>
                                        <Typography variant="caption" fontWeight={600} color="success.main" sx={{ fontSize: '0.75rem' }}>
                                            {formatPrice(paxSellTotal)}
                                        </Typography>
                                    </Stack>
                                </Box>
                            );
                        })}

                        <Box sx={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            px: 1.5, py: 0.75, borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                        }}>
                            <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                                {isArabic ? 'إجمالي الفندق الأساسي' : 'Base Hotel Total'}
                            </Typography>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {formatPrice(stayTotals.total_cost - extras.reduce((s, e) => {
                                        const amt = calculateExtraAmount(e, rooms, hotelNights, totalPax);
                                        return s + amt.cost_price;
                                    }, 0))} /
                                </Typography>
                                <Typography variant="caption" fontWeight={700} color="success.main" sx={{ fontSize: '0.75rem' }}>
                                    {formatPrice(stayTotals.total_price - extras.reduce((s, e) => {
                                        const amt = calculateExtraAmount(e, rooms, hotelNights, totalPax);
                                        return s + amt.sell_price;
                                    }, 0))}
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
            )}

            {/* Per-day extras */}
            {extras.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {isArabic ? 'الإضافات اليومية' : 'Per-Day Extras'}
                    </Typography>
                    <Stack spacing={0.5}>
                        {extras.map((extra, i) => {
                            const amounts = calculateExtraAmount(extra, rooms, hotelNights, totalPax);
                            const basisLabels = {
                                once: isArabic ? 'مرة واحدة' : 'Once',
                                per_night: isArabic ? 'كل ليلة' : 'Per Night',
                                per_room: isArabic ? 'كل غرفة' : 'Per Room',
                                room_night: isArabic ? 'غرفة/ليلة' : 'Room/Night',
                                per_person: isArabic ? 'للشخص' : 'Per Person',
                            };
                            return (
                                <Box
                                    key={extra.id || i}
                                    sx={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        px: 1.5, py: 0.75, borderRadius: 1,
                                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', flex: 1 }}>
                                        {(() => {
                                            const rawExtraName = extra.name || extra.extra_name || extraNameMap[String(extra.hotel_extra_id)];
                                            const extraName = typeof rawExtraName === 'object'
                                                ? (rawExtraName?.[isArabic ? 'ar' : 'en'] || rawExtraName?.en || rawExtraName?.ar || '')
                                                : (typeof rawExtraName === 'string' ? rawExtraName : '');
                                            return extraName || (isArabic ? `إضافة ${i + 1}` : `Extra ${i + 1}`);
                                        })()}
                                        <Chip
                                            label={basisLabels[extra.pricing_basis] || extra.pricing_basis}
                                            size="small"
                                            sx={{ ml: 0.5, height: 18, fontSize: '0.6rem' }}
                                            variant="outlined"
                                        />
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, fontSize: '0.65rem' }}>
                                            {(() => {
                                                const q = extra.quantity || 1;
                                                const b = extra.pricing_basis;
                                                if (b === 'once') return `×${q}`;
                                                const nights = extra.applicable_dates?.length || extra.days_count || hotelNights;
                                                if (b === 'per_night') return `×${q * nights}`;
                                                if (b === 'per_room') return `×${q * rooms}`;
                                                if (b === 'room_night') return `×${q * nights * rooms}`;
                                                if (b === 'per_person') return `×${q * totalPax}`;
                                                return `×${q}`;
                                            })()}
                                        </Typography>
                                    </Typography>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {formatPrice(amounts.cost_price)} /
                                        </Typography>
                                        <Typography variant="caption" fontWeight={600} color="success.main" sx={{ fontSize: '0.75rem' }}>
                                            {formatPrice(amounts.sell_price)}
                                        </Typography>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </>
    );
};

const HotelQuotationCard = ({
    hotel,
    hotelIndex,
    errors,
    isArabic,
    paxTypes,
    formData,
    onUpdateHotelPricing,
}) => {
    const theme = useTheme();
    const isRoomNightDaily = hotel.room_stays?.length > 0;

    const hotelName = hotel.hotel_name || (isArabic ? 'فندق' : 'Hotel');
    // const totalPax = useMemo(() =>
    //     (paxTypes || []).reduce((sum, pt) => sum + (pt.count || 0), 0),
    //     [paxTypes]
    // );

    const stayTotals = useMemo(() =>
        calculateHotelStayTotals(hotel, paxTypes, formData.itinerary),
        [hotel, paxTypes, formData.itinerary]
    );

    const extras = useMemo(() =>
        getExtrasForStay(hotel, formData.itinerary),
        [hotel, formData.itinerary]
    );
    const { options: extraOptions } = useGetHotelExtrasForSelect();
    const extraNameMap = useMemo(() => {
        const map = {};
        (extraOptions || []).forEach(opt => {
            map[String(opt.value ?? opt.id)] = opt.label || opt.name || '';
        });
        return map;
    }, [extraOptions]);

    const rooms = isRoomNightDaily
        ? hotel.room_stays.reduce((max, rs) => Math.max(max, rs.rooms_count || 1), 0)
        : (hotel.rooms || 1);
    const hotelNights = isRoomNightDaily ? hotel.room_stays.length : (hotel.nights || 1);

    const profit = stayTotals.total_price - stayTotals.total_cost;
    const profitMargin = stayTotals.total_price > 0
        ? ((profit / stayTotals.total_price) * 100).toFixed(1)
        : 0;

    const { options: roomCategories } = useGetHotelRoomCategoriesForSelect();
    const roomCategoryName = useMemo(() => {
        let catId = hotel.room_category_id;
        if (!catId && isRoomNightDaily && hotel.room_stays.length > 0) {
            catId = hotel.room_stays[0].room_category_id;
        }
        if (catId) {
            const cat = roomCategories?.find(c => String(c.value ?? c.id) === String(catId));
            if (cat) return cat.label || cat.name || '';
        }
        if (isRoomNightDaily && hotel.room_stays.length > 0) {
            const rsName = hotel.room_stays[0].room_category_name;
            return rsName?.[isArabic ? 'ar' : 'en'] || rsName?.en || rsName?.ar || '';
        }
        return '';
    }, [hotel, roomCategories, isArabic, isRoomNightDaily]);

    const PriceView = isRoomNightDaily ? RoomNightDailyView : LegacyPerPersonNightView;

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1.5px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                overflow: 'hidden',
                bgcolor: alpha(theme.palette.secondary.main, 0.02),
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <HotelIcon color="secondary" sx={{ fontSize: 20 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                                {hotelName}
                            </Typography>
                            {hotel.pricing?.is_price_edited && (
                                <Chip
                                    icon={<EditIcon sx={{ fontSize: 12 }} />}
                                    label={isArabic ? 'معدل' : 'Edited'}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600 }}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mt: 0.5, display: 'inline-block' }}>
                            {formatDate(hotel.check_in_date) || ''} → {formatDate(hotel.check_out_date) || ''} 
                            {roomCategoryName ? ` — ${roomCategoryName}` : ''}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                        <Chip
                            icon={<BedIcon sx={{ fontSize: 14 }} />}
                            label={`${hotelNights} ${isArabic ? 'ليلة' : 'Nights'}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ height: 24, fontSize: '0.7rem' }}
                        />
                        <Chip
                            icon={<RoomIcon sx={{ fontSize: 14 }} />}
                            label={`${rooms} ${isArabic ? 'غرف' : 'Rooms'}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ height: 24, fontSize: '0.7rem' }}
                        />
                        {isRoomNightDaily && (
                            <Chip
                                label="Daily"
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ height: 24, fontSize: '0.65rem' }}
                            />
                        )}
                    </Stack>
                </Stack>
            </Box>

            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <PriceView
                    hotel={hotel}
                    hotelIndex={hotelIndex}
                    isArabic={isArabic}
                    paxTypes={paxTypes}
                    onUpdateHotelPricing={onUpdateHotelPricing}
                    stayTotals={stayTotals}
                    extras={extras}
                    extraNameMap={extraNameMap}
                    theme={theme}
                />

                {/* Totals */}
                <Divider sx={{ mb: 1.5 }} />
                <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 1.5, py: 1, borderRadius: 1,
                    bgcolor: alpha(theme.palette.secondary.main, 0.06),
                }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
                            {isArabic ? 'الإجمالي' : 'Total'}
                        </Typography>
                        <Chip
                            label={`${profitMargin}%`}
                            size="small"
                            color={profit >= 0 ? 'success' : 'error'}
                            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700 }}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                {isArabic ? 'سعر البيع' : 'Sell Price'}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.95rem' }}>
                                {formatPrice(stayTotals.total_price)}
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                {isArabic ? 'التكلفة' : 'Cost'}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                                {formatPrice(stayTotals.total_cost)}
                            </Typography>
                        </Box>
                        <ExpectedProfitLabel
                            profit={profit}
                            isArabic={isArabic}
                            label={isArabic ? 'الربح' : 'Profit'}
                            margin={profitMargin}
                            sx={{ minWidth: 140, mb: 0 }}
                        />
                    </Stack>
                </Box>
            </CardContent>
        </Card>
    );
};

export default HotelQuotationCard;